import { Loader } from "lucide-react";

import { Cancel, CheckMark } from "@/assets/icons";
import {
    AccountHierarchyEnum,
    BadgeStateEnum,
    BalanceEnum,
    DataSourceStatusEnum,
    DataSourceSyncEnum,
    DataSourceSyncFrequencyEnum,
    EmailVerificationEnum,
    OfflineDataSourceAmountFieldConditionEnum,
    OfflineDataSourceAmountFieldDataActionEnum,
    OfflineDataSourceDateTimeFieldDataActionEnum,
    OfflineDataSourceDateTimeFieldDateFormatEnum,
    OfflineDataSourceDateTimeFieldTimeFormatEnum,
    OfflineDataSourceNameFieldDataActionEnum,
    OfflineDataSourceNameFieldSeparatorEnum,
    OrganizationEmployeesNumberLabelEnum,
    OrganizationStatusEnum,
    OrganizationSubscriptionPlanEnum,
    ReconciliationJobEnum,
    ReconciliationStatusEnum,
    RoleEnum,
    StatusEnum,
    TimePartsEnum,
    TransactionAmountEnum,
    TransactionStatusEnum,
} from "@/enums";

import { Badge } from "../atoms/badge";
import { constantsData } from "./constants";

export const optionsData = {
    account: {
        hierarchies: [AccountHierarchyEnum.PARENT, AccountHierarchyEnum.SUB],
        status: {
            badges: {
                [StatusEnum.ACTIVE]: <Badge state={BadgeStateEnum.SUCCESS}>{constantsData.labels.active}</Badge>,
                [StatusEnum.INACTIVE]: <Badge state={BadgeStateEnum.DANGER}>{constantsData.labels.inactive}</Badge>,
            },
            options: [StatusEnum.ACTIVE, StatusEnum.INACTIVE],
        },
    },
    balanceTypes: [BalanceEnum.DEBIT, BalanceEnum.CREDIT],
    dataSource: {
        calculate: {
            conditions: [
                OfflineDataSourceAmountFieldConditionEnum.PLUS,
                OfflineDataSourceAmountFieldConditionEnum.MINUS,
            ],
            transactionAmountTypes: [
                TransactionAmountEnum.NET,
                TransactionAmountEnum.GROSS,
                TransactionAmountEnum.FEE,
            ],
        },
        connectionTypes: [DataSourceSyncEnum.ONLINE, DataSourceSyncEnum.OFFLINE],
        status: {
            badges: {
                [DataSourceStatusEnum.ACTIVE]: <Badge state={BadgeStateEnum.SUCCESS}>{constantsData.labels.active}</Badge>,
                [DataSourceStatusEnum.INACTIVE]: <Badge state={BadgeStateEnum.DANGER}>{constantsData.labels.inactive}</Badge>,
                [DataSourceStatusEnum.SETTING_UP]: <Badge state={BadgeStateEnum.INPROGRESS}>{constantsData.labels.settingUp}</Badge>,
            },
            options: [DataSourceStatusEnum.ACTIVE, DataSourceStatusEnum.INACTIVE, DataSourceStatusEnum.SETTING_UP],
        },
        syncFrequencies: [DataSourceSyncFrequencyEnum.FREQUENCY_MANUAL, DataSourceSyncFrequencyEnum.FREQUENCY_12_HOURS, DataSourceSyncFrequencyEnum.FREQUENCY_24_HOURS],
        transform: {
            amount: {
                dataActions: [
                    OfflineDataSourceAmountFieldDataActionEnum.ARITHMETIC,
                    OfflineDataSourceAmountFieldDataActionEnum.CONDITIONAL,
                    OfflineDataSourceAmountFieldDataActionEnum.SEPARATE,
                ],
            },
            dateTime: {
                dataActions: [OfflineDataSourceDateTimeFieldDataActionEnum.COMBINE],
                dateFormats: Object.values(OfflineDataSourceDateTimeFieldDateFormatEnum).map((fullText) => ({
                    id: fullText.split(" (")[0],
                    name: fullText,
                })),
                timeFormat: Object.values(OfflineDataSourceDateTimeFieldTimeFormatEnum).map((fullText) => ({
                    id: fullText.split(" (")[0],
                    name: fullText,
                })),
            },
            name: {
                dataActions: [OfflineDataSourceNameFieldDataActionEnum.COMBINE],
                separators: [
                    OfflineDataSourceNameFieldSeparatorEnum.DASH,
                    OfflineDataSourceNameFieldSeparatorEnum.COMMA,
                    OfflineDataSourceNameFieldSeparatorEnum.SLASH,
                ],
            },
        },
    },
    emailVerifications: [EmailVerificationEnum.VERIFIED, EmailVerificationEnum.UNVERIFIED],
    organization: {
        employeeNumbers: [
            OrganizationEmployeesNumberLabelEnum.ONE_TO_TEN_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.ELEVEN_TO_FIFTY_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.FIFTY_ONE_TO_TWO_HUNDRED_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.TWO_HUNDRED_TO_FIVE_HUNDRED_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.FIVE_HUNDRED_TO_THOUSAND_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.THOUSAND_TO_FIVE_THOUSAND_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.FIVE_THOUSAND_TO_TEN_THOUSAND_EMPLOYEES,
            OrganizationEmployeesNumberLabelEnum.TEN_THOUSAND_PLUS,
        ],
        status: {
            badges: {
                [OrganizationStatusEnum.ACTIVE]: <Badge state={BadgeStateEnum.SUCCESS}>{constantsData.labels.active}</Badge>,
                [OrganizationStatusEnum.SUSPENDED]: <Badge state={BadgeStateEnum.DANGER}>{constantsData.labels.suspended}</Badge>,
            },
            options: [OrganizationStatusEnum.ACTIVE, OrganizationStatusEnum.SUSPENDED],
        },
        subscriptionPlans: [OrganizationSubscriptionPlanEnum.ACTIVE, OrganizationSubscriptionPlanEnum.TRIAL, OrganizationSubscriptionPlanEnum.CANCELED],
        windowSizes: [TimePartsEnum.MINUTES, TimePartsEnum.HOURS, TimePartsEnum.DAYS],
    },
    reconciliation: {
        frequencies: [DataSourceSyncFrequencyEnum.FREQUENCY_MANUAL, DataSourceSyncFrequencyEnum.FREQUENCY_12_HOURS, DataSourceSyncFrequencyEnum.FREQUENCY_24_HOURS],
        job: {
            badges: {
                [ReconciliationJobEnum.COMPLETED]: (
                    <Badge state={BadgeStateEnum.SUCCESS}>
                        <CheckMark size={16} />
                        {" "}
                        {constantsData.labels.completed}
                    </Badge>
                ),
                [ReconciliationJobEnum.PENDING]: <Badge state={BadgeStateEnum.WARNING}>{constantsData.labels.pending}</Badge>,
                [ReconciliationJobEnum.RUNNING]: (
                    <Badge state={BadgeStateEnum.INPROGRESS}>
                        {constantsData.labels.running}
                        {" "}
                        <Loader
                            className="animate-spin"
                            size={16}
                        />
                    </Badge>
                ),
                [ReconciliationJobEnum.FAILED]: (
                    <Badge state={BadgeStateEnum.DANGER}>
                        <Cancel size={16} />
                        {" "}
                        {constantsData.labels.failed}
                    </Badge>
                ),
            },
            options: [
                ReconciliationJobEnum.COMPLETED,
                ReconciliationJobEnum.PENDING,
                ReconciliationJobEnum.RUNNING,
                ReconciliationJobEnum.FAILED,
            ],
        },
        status: {
            badges: {
                [ReconciliationStatusEnum.MATCHED]: <Badge state={BadgeStateEnum.SUCCESS}>{constantsData.labels.matched}</Badge>,
                [ReconciliationStatusEnum.PARTIALLY_MATCHED]: <Badge state={BadgeStateEnum.COMPLETED_PARTIALLY}>{constantsData.labels.partiallyMatched}</Badge>,
                [ReconciliationStatusEnum.NOT_PROCESSED]: <Badge state={BadgeStateEnum.NOT_PROCESSED}>{constantsData.labels.notProcessed}</Badge>,
                [ReconciliationStatusEnum.REVIEW]: <Badge state={BadgeStateEnum.WARNING}>{constantsData.labels.review}</Badge>,
                [ReconciliationStatusEnum.EXCEPTIONS]: <Badge state={BadgeStateEnum.DANGER}>{constantsData.labels.exceptions}</Badge>,
                [ReconciliationStatusEnum.UNMATCHED]: <Badge state={BadgeStateEnum.DANGER}>{constantsData.labels.unmatched}</Badge>,
            },
            options: [
                ReconciliationStatusEnum.MATCHED,
                ReconciliationStatusEnum.PARTIALLY_MATCHED,
                ReconciliationStatusEnum.NOT_PROCESSED,
                ReconciliationStatusEnum.REVIEW,
                ReconciliationStatusEnum.EXCEPTIONS,
            ],
        },
    },
    roles: [RoleEnum.ADMIN, RoleEnum.MODERATOR, RoleEnum.VIEWER, RoleEnum.SUPER_ADMIN],
    transaction: {
        status: {
            badges: {
                [TransactionStatusEnum.PENDING]: (
                    <Badge
                        state={BadgeStateEnum.INPROGRESS}
                        variant="outline"
                    >
                        {constantsData.labels.pending}
                    </Badge>
                ),
                [TransactionStatusEnum.POSTED]: (
                    <Badge
                        state={BadgeStateEnum.SUCCESS}
                        variant="outline"
                    >
                        {constantsData.labels.posted}
                    </Badge>
                ),
                [TransactionStatusEnum.ERROR]: (
                    <Badge
                        state={BadgeStateEnum.DANGER}
                        variant="outline"
                    >
                        {constantsData.labels.error}
                    </Badge>
                ),
                [TransactionStatusEnum.IGNORED]: (
                    <Badge
                        state={BadgeStateEnum.DANGER}
                        variant="outline"
                    >
                        {constantsData.labels.ignored}
                    </Badge>
                ),
            },
            options: [TransactionStatusEnum.PENDING, TransactionStatusEnum.POSTED],
        },
    },
};
